import { useMutation } from '@tanstack/react-query';
import useGetMarketingData from './useGetMarketingData';
import {
  copyProjectEmailTemplate,
  invalidateEmailTemplates,
} from '../../services/marketing';
import { CreateTemplateAndEmbedResponse } from '../../types/marketing';

interface Payload {
  sourceProjectTemplateId: string;
}

interface Options {
  onSuccess?: (result: CreateTemplateAndEmbedResponse) => void;
  onError?: () => void;
  onMutate?: (payload: Payload) => void;
  onSettled?: () => void;
}

function useCopyEmailProjectTemplate({
  onSuccess,
  onError,
  onMutate,
  onSettled,
}: Options = {}) {
  const { data, isFetching } = useGetMarketingData();

  return useMutation({
    mutationFn: async ({ sourceProjectTemplateId }: Payload) => {
      if (!data?.id || isFetching) return null;

      const result = await copyProjectEmailTemplate(
        data.id,
        sourceProjectTemplateId,
      );
      if (!result) throw new Error('Failed to copy project email template');

      return result;
    },
    onSuccess: async result => {
      if (!result) return;
      await invalidateEmailTemplates();
      onSuccess?.(result);
    },
    onError: () => {
      onError?.();
    },
    onMutate,
    onSettled,
  });
}

export default useCopyEmailProjectTemplate;
